import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './styles.css';
import { useModalFocusReturn } from '../../hooks/useModalFocusReturn';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

// Simple list-style popup modal showing every group affiliation for a provider, used
// when there are more than can comfortably be shown inline (see providerDetails.jsx's
// "show all group affiliations" link). Mirrors handicapAccessibilityModal.jsx's
// structure/focus-trap/escape-key/scroll-lock behavior.
const GroupAffiliationsModal = ({ isOpen, onClose, affiliations = [], title = 'Group affiliations' }) => {
  const modalRef = useRef(null);

  // Restore focus to whatever triggered this modal once it closes (declared before
  // the effect below so it captures the trigger element BEFORE that effect moves
  // focus onto the first focusable element inside the modal).
  useModalFocusReturn(isOpen);

  // Lock page scroll while open, without jumping to the top and correctly restoring
  // the exact scroll position on close.
  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    const focusableElements = modalRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    const handleEscapeKey = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleTabKey);
    document.addEventListener('keydown', handleEscapeKey);
    firstElement?.focus({ preventScroll: true });

    return () => {
      document.removeEventListener('keydown', handleTabKey);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="group-affiliations-modal-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="group-affiliations-modal-title"
    >
      <div className="group-affiliations-modal" ref={modalRef}>
        <button
          className="group-affiliations-modal-close"
          onClick={onClose}
          aria-label="Close modal"
          type="button"
        >
          ×
        </button>

        <div className="group-affiliations-modal-content">
          <h2 id="group-affiliations-modal-title" className="group-affiliations-modal-title">
            {title}
          </h2>

          {affiliations.length > 0 ? (
            <ul className="group-affiliations-list">
              {affiliations.map((affiliation, index) => (
                <li key={index} className="group-affiliations-item">
                  {affiliation}
                </li>
              ))}
            </ul>
          ) : (
            <p className="group-affiliations-empty">None found</p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default GroupAffiliationsModal;
